import { useEffect, useState } from "react"

const isPlainObject = value =>
  value !== null && typeof value === "object" && !Array.isArray(value)

const pathSegment = key => {
  if (/^[A-Za-z_$][A-Za-z0-9_$]*$/.test(key)) return key
  return `['${String(key).replace(/'/g, "\\'")}']`
}

const appendPath = (base, key) => {
  const segment = pathSegment(key)
  if (!base) return segment
  return segment.startsWith("[") ? `${base}${segment}` : `${base}.${segment}`
}

// Maps in the sample payload that are keyed by a runtime identifier rather
// than a fixed property name. We substitute the variable name so the
// autocomplete suggests e.g. `stopIdsV2[stopId].orderId` instead of
// `stopIdsV2['test-st-000088-12'].orderId`.
const ID_KEYED_MAPS = {
  stopIdsV2: "stopId",
}

const sampleValueLabel = value => {
  if (value === null) return "null"
  if (value === undefined) return ""
  if (typeof value === "string") return value
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  return ""
}

export const flattenPayloadPaths = (value, basePath = "") => {
  if (Array.isArray(value)) {
    if (value.length === 0) return []
    return flattenPayloadPaths(value[0], `${basePath}[0]`)
  }

  if (isPlainObject(value)) {
    const keys = Object.keys(value)
    const lastSegment = basePath.split(/[.[]/).pop()?.replace(/]$/, "")
    const varName = ID_KEYED_MAPS[lastSegment]
    if (varName && keys.length > 0) {
      return flattenPayloadPaths(value[keys[0]], `${basePath}[${varName}]`)
    }
    return keys.flatMap(key =>
      flattenPayloadPaths(value[key], appendPath(basePath, key))
    )
  }

  if (!basePath) return []

  return [{
    path: basePath,
    value,
    label: sampleValueLabel(value),
  }]
}

// One request per URL for the life of the page, shared by every caller. A single value editor in
// a modal made one fetch either way; a table of them made one per row.
const samplePaths = new Map()

const loadSamplePaths = sampleUrl => {
  if (!samplePaths.has(sampleUrl)) {
    samplePaths.set(sampleUrl, fetch(sampleUrl)
      .then(response => (response.ok ? response.json() : null))
      .then(payload => {
        if (!payload) return []
        const flattened = flattenPayloadPaths(payload)
        return Array.from(new Map(flattened.map(item => [item.path, item])).values())
      })
      .catch(error => {
        console.error("Unable to load sample payload", error)
        // not cached as a rejection, so a later mount can try again
        samplePaths.delete(sampleUrl)
        return []
      }))
  }
  return samplePaths.get(sampleUrl)
}

export const useSamplePayloadPaths = (sampleUrl = "/opendock_sample_event.json") => {
  const [paths, setPaths] = useState([])

  useEffect(() => {
    let active = true
    loadSamplePaths(sampleUrl).then(loaded => {
      if (active) setPaths(loaded)
    })
    return () => {
      active = false
    }
  }, [sampleUrl])

  return paths
}
